import {
  type ColumnDef,
  getCoreRowModel,
  type InitialTableState,
  type Row,
  type RowData,
  type RowSelectionState,
  type SortingState,
  type TableOptions,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { cva, type VariantProps } from "class-variance-authority";
import { useRef } from "react";

import { cn } from "../../utils";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "../table";
import { DataTableHeaderCell } from "./data-table-header-cell";
import { MemoizedTableRow } from "./data-table-row";
import type { IFilterOption } from "./filters/MultiSelectFilterInput";
import type { FiltersState } from "./filters/types";

export type ColumnFilterType =
  | "text"
  | "date"
  | "multiSelect"
  | "numberRange"
  | "creatableMultiSelect";

const dataTableVariants = cva(
  [
    "table-fixed border-separate border-spacing-0 [&_td]:border-gray-200 [&_tfoot_td]:border-t [&_th]:border-b [&_th]:border-gray-200 [&_tr:not(:last-child)_td]:border-b [&_tr]:border-none",
  ].join(" "),
  {
    variants: {
      spacing: {
        sm: "[&_th]:px-1 [&_th]:py-2 [&_td]:px-1 [&_td]:py-2",
        md: "[&_th]:px-2 [&_th]:py-2 [&_td]:px-2 [&_td]:py-4",
        lg: "[&_th]:px-3 [&_th]:py-6 [&_td]:px-3 [&_td]:py-6",
      },
    },
    defaultVariants: {
      spacing: "md",
    },
  },
);

const surfaceVariants = cva("overflow-hidden rounded-md bg-white", {
  variants: {
    surface: {
      bordered: "border border-gray-200",
      borderless: "",
      shadow: "shadow-lg p-4",
    },
  },
});

export type ColumnType = "string" | "number" | "boolean" | "date";

declare module "@tanstack/table-core" {
  interface ColumnMeta<TData extends RowData, TValue> {
    headerClassName?: string;
    cellClassName?: string;
    /** Human-readable column label used in sort/filter badges. */
    label?: string;
    filterType?: ColumnFilterType;
    columnType?: ColumnType;
    /** Options for multiSelect filters. `label` may be a ReactNode for rich display. */
    filterOptions?: IFilterOption[];
  }
}

interface IDataTableProps<TData extends RowData>
  extends Pick<
      TableOptions<TData>,
      "onRowSelectionChange" | "onSortingChange" | "getRowId"
    >,
    VariantProps<typeof dataTableVariants>,
    VariantProps<typeof surfaceVariants> {
  columns: ColumnDef<TData>[];
  data: TData[];
  sorting?: SortingState;
  selection?: RowSelectionState;
  columnVisibility?: VisibilityState;
  enableRowSelection?: boolean;
  enableMultiSort?: boolean;
  initialTableState?: InitialTableState;
  getRowClassName?: (row: TData) => string | undefined;
  /** Enables row virtualization for large lists. */
  virtualRows?: boolean;
  /** Active column filters keyed by column id. */
  filters?: FiltersState;
  /** Called whenever a column filter changes. */
  onFiltersChange?: (filters: FiltersState) => void;

  className?: string;
}

export function DataTable<TData extends RowData>({
  columns,
  data,
  sorting,
  selection = {},
  enableRowSelection = false,
  enableMultiSort = false,
  initialTableState,
  getRowClassName,
  virtualRows = false,
  filters,
  onFiltersChange,
  columnVisibility,
  spacing = "md",
  surface = "shadow",
  className,
  ...rest
}: IDataTableProps<TData>) {
  const table = useReactTable<TData>({
    data,
    columns,
    state: { sorting, rowSelection: selection, columnVisibility },
    initialState: initialTableState,
    enableRowSelection,
    enableMultiSort,
    enableSorting: true,
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
    ...rest,
  });

  const tableBodyRef = useRef<HTMLTableSectionElement>(null);
  const rows = table.getRowModel().rows;

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () =>
      tableBodyRef.current?.closest("[data-scroll]") ?? null,
    estimateSize: () => 40,
    enabled: virtualRows,
  });

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div className={cn(surfaceVariants({ surface }), className)}>
      <Table className={cn(dataTableVariants({ spacing }))}>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="bg-gray-100/50">
              {headerGroup.headers.map((header) => (
                <DataTableHeaderCell
                  key={header.id}
                  header={header}
                  sorting={sorting}
                  filters={filters}
                  onFiltersChange={onFiltersChange}
                  onSortingChange={rest.onSortingChange}
                />
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody ref={tableBodyRef}>
          {rows.length ? (
            virtualRows ? (
              virtualItems.map((vRow) => {
                const row = rows[vRow.index] as Row<TData>;
                if (!row) return null;
                return (
                  <MemoizedTableRow
                    key={row.id}
                    row={row}
                    enableRowSelection={enableRowSelection}
                    getRowClassName={getRowClassName}
                  />
                );
              })
            ) : (
              rows.map((row) => (
                <MemoizedTableRow
                  key={row.id}
                  row={row}
                  enableRowSelection={enableRowSelection}
                  getRowClassName={getRowClassName}
                />
              ))
            )
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
